import { useState, useCallback, useRef, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './DatePickerInput.css';
import { createPortal } from 'react-dom';

interface IDatePickerInput {
  value?: Date | null;
  onChange?: (value: Date | null) => void;
  width?: number | string;
  textAlign?: 'left' | 'center' | 'right';
  placeholder?: string;
  style?: React.CSSProperties;
  className?: string;
  disabled?: boolean;
}

function formatDate(date: Date | null | undefined): string {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}.${month}.${day}`;
}

function formatInputDate(input: string): string {
  const digitsOnly = input.replace(/\D/g, '');
  if (digitsOnly.length <= 4) {
    return digitsOnly;
  } else if (digitsOnly.length <= 6) {
    return `${digitsOnly.slice(0, 4)}.${digitsOnly.slice(4)}`;
  } else {
    return `${digitsOnly.slice(0, 4)}.${digitsOnly.slice(4, 6)}.${digitsOnly.slice(6, 8)}`;
  }
}

function parseDate(dateString: string): Date | null {
  const regex = /^(\d{4})\.(\d{2})\.(\d{2})$/;
  const match = dateString.match(regex);
  if (!match) return null;

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1;
  const day = parseInt(match[3], 10);
  const date = new Date(year, month, day);

  if (isNaN(date.getTime())) return null;
  return date;
}

const DatePickerInput = ({
  value,
  onChange,
  width = 140,
  textAlign = 'left',
  placeholder = 'YYYY.MM.DD',
  style,
  className,
  disabled = false,
}: IDatePickerInput) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(value || null);
  const [inputValue, setInputValue] = useState(formatDate(value));
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedDate(value || null);
    setInputValue(formatDate(value));
  }, [value]);

  useEffect(() => {
    if (showCalendar && wrapperRef.current) {
      const updatePosition = () => {
        const wrapperRect = wrapperRef.current!.getBoundingClientRect();
        const isMobile = window.innerWidth < 768;
        const calendarWidth = 280;
        
        if (isMobile) {
          // 모바일: 화면 중앙에 띄우기
          setDropdownStyle({
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
          });
        } else {
          // 데스크톱: input 아래에 띄우기 (화면 밖으로 안 나가게)
          let left = wrapperRect.left;
          if (left + calendarWidth > window.innerWidth) {
            left = window.innerWidth - calendarWidth - 10;
          }
          setDropdownStyle({
            position: 'fixed',
            top: `${wrapperRect.bottom + 5}px`,
            left: `${left}px`,
            zIndex: 9999,
          });
        }
      };

      updatePosition();
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showCalendar]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        const calendarElement = document.querySelector('.saju-datepicker-portal');
        if (calendarElement && !calendarElement.contains(event.target as Node)) {
          setShowCalendar(false);
        }
      }
    };

    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {


        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showCalendar]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawInput = e.target.value;
      const formatted = formatInputDate(rawInput);
      setInputValue(formatted);

      // 입력값이 비어있으면 null 전달
      if (formatted === '' || rawInput.trim() === '') {
        setSelectedDate(null);
        onChange?.(null);
        return;
      }

      const parsedDate = parseDate(formatted);
      if (parsedDate) {
        setSelectedDate(parsedDate);
        onChange?.(parsedDate);
      }
    },
    [onChange]
  );

  const handleCalendarSelect = useCallback(
    (date: Date) => {
      setSelectedDate(date);
      setInputValue(formatDate(date));
      onChange?.(date);
      setShowCalendar(false);
    },
    [onChange]
  );

  const toggleCalendar = useCallback(() => {
    if (!disabled) {
      setShowCalendar(prev => !prev);
    }
  }, [disabled]);

  // 월 단위 네비게이션을 위한 activeStartDate 상태
  const [activeStartDate, setActiveStartDate] = useState<Date>(value || new Date());
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);

  // value prop 변경 시 activeStartDate 동기화
  useEffect(() => {
    if (value) {
      setActiveStartDate(value);
    }
  }, [value]);

  const handlePrevMonth = useCallback(() => {
    setActiveStartDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  }, []);

  const handleNextMonth = useCallback(() => {
    setActiveStartDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  }, []);

  const handleYearSelect = useCallback((year: number) => {
    setActiveStartDate(prev => {
      const newDate = new Date(prev);
      newDate.setFullYear(year);
      return newDate;
    });
    setShowYearDropdown(false);
  }, []);

  const handleMonthSelect = useCallback((month: number) => {
    setActiveStartDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(month);
      return newDate;
    });
    setShowMonthDropdown(false);
  }, []);

  // 년도 옵션 생성 (현재 년도 기준 ±20년)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 41 }, (_, i) => currentYear - 20 + i);
  const monthOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  return (
    <div
      ref={wrapperRef}
      style={{ width, ...style }}
      className={`!relative ${className || ''} ${disabled ? '!opacity-50' : ''}`}
    >
      <div className="!relative">
        <input
          type="text"
          style={{ textAlign }}
          value={inputValue}
          placeholder={placeholder}
          className="!w-full !px-3 !py-2 !pr-10 !border !border-gray-300 !rounded-md !shadow-sm !text-sm !font-normal !text-gray-900 placeholder:!text-gray-400 placeholder:!text-sm placeholder:!font-normal focus:!outline-none focus:!border-blue-400 disabled:!bg-gray-50 disabled:!text-gray-500 !bg-white !transition-colors !duration-200"
          onChange={handleInputChange}
          disabled={disabled}
        />
        <button
          type="button"
          onClick={toggleCalendar}
          disabled={disabled}
          className="!absolute !right-2 !top-1/2 !-translate-y-1/2 !text-gray-400 hover:!text-gray-600 !transition-colors"
        >
          <svg className="!w-5 !h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>
      </div>
      {showCalendar &&
        createPortal(
          <>
            {/* 모바일 배경 오버레이 */}
            <div 
              className="md:!hidden !fixed !inset-0 !bg-black/50 !z-[9998]"
              onClick={() => setShowCalendar(false)}
            />
            <div
              style={{ ...dropdownStyle, minWidth: '280px' }}
              className="saju-datepicker-portal !bg-white !border !border-gray-200 !rounded-lg !shadow-lg !p-4"
            >
              <div className="!flex !items-center !justify-between !mb-2">
                <button
                  type="button"
                  onClick={handlePrevMonth}
                  className="!w-8 !h-8 !flex !items-center !justify-center !text-gray-600 hover:!bg-gray-100 !rounded !text-lg !font-bold"
                >
                  {'<'}
                </button>
                <div className="!flex !items-center !gap-1">
                  {/* 년도 드롭다운 */}
                  <div className="!relative">
                    <button
                      type="button"
                      onClick={() => { setShowYearDropdown(!showYearDropdown); setShowMonthDropdown(false); }}
                      className="!px-2 !py-1 !font-semibold !text-sm hover:!bg-gray-100 !rounded !transition-colors !flex !items-center !gap-0.5 !border !border-gray-300"
                    >
                      {activeStartDate.getFullYear()}년
                      <svg className="!w-3 !h-3 !text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {showYearDropdown && (
                      <div className="!absolute !top-full !left-0 !mt-1 !bg-white !border !border-gray-300 !rounded-lg !max-h-48 !overflow-y-auto !z-50" style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)' }}>
                        {yearOptions.map(year => (
                          <button
                            key={year}
                            type="button"
                            onClick={() => handleYearSelect(year)}
                            className={`!block !w-full !px-4 !py-1.5 !text-sm !text-center hover:!bg-gray-100 ${
                              year === activeStartDate.getFullYear() ? '!bg-blue-50 !text-blue-600 !font-semibold' : ''
                            }`}
                          >
                            {year}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* 월 드롭다운 */}
                  <div className="!relative">
                    <button
                      type="button"
                      onClick={() => { setShowMonthDropdown(!showMonthDropdown); setShowYearDropdown(false); }}
                      className="!px-2 !py-1 !font-semibold !text-sm hover:!bg-gray-100 !rounded !transition-colors !flex !items-center !gap-0.5 !border !border-gray-300"
                    >
                      {activeStartDate.getMonth() + 1}월
                      <svg className="!w-3 !h-3 !text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {showMonthDropdown && (
                      <div className="!absolute !top-full !left-0 !mt-1 !bg-white !border !border-gray-300 !rounded-lg !max-h-48 !overflow-y-auto !z-50" style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)' }}>
                        {monthOptions.map(month => (
                          <button
                            key={month}
                            type="button"
                            onClick={() => handleMonthSelect(month - 1)}
                            className={`!block !w-full !px-4 !py-1.5 !text-sm !text-center hover:!bg-gray-100 ${
                              month - 1 === activeStartDate.getMonth() ? '!bg-blue-50 !text-blue-600 !font-semibold' : ''
                            }`}
                          >
                            {month}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleNextMonth}
                  className="!w-8 !h-8 !flex !items-center !justify-center !text-gray-600 hover:!bg-gray-100 !rounded !text-lg !font-bold"
                >
                  {'>'}
                </button>
              </div>
              <Calendar
                onChange={date => handleCalendarSelect(date as Date)}
                value={selectedDate}
                activeStartDate={activeStartDate}
                locale="ko-KR"
                maxDetail="month"
                minDetail="month"
                showNavigation={false}
                formatDay={(locale, date) => date.getDate().toString()}
                className="!w-[252px]"
              />
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

export default DatePickerInput;